import { useState, useEffect, useCallback } from "react";
import { useRevalidator } from "react-router-dom";

import useService from "../../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../../global/ApiRequestErrorHandler";
import ModalShell from "../../../../../../global/ModalShell";
import DynamicForm from "../../../../../dynamic-forms/DynamicForm";
import Details from "../../DetailBase/Details";
import Title from "../../DetailBase/Title";

export default function AssociateDocumentsWrapper({
  document,
  openAssociateAppointmentsModal,
  handleCloseAssociateAppointmentModal,
}) {
  const { revalidate } = useRevalidator();
  const dynamicFormRepository = useService("repositories/dynamic-form-repository");
  const [dynamicForm, setDynamicForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleErrorMessage = (err) => {
    const errArr = ApiRequestErrorHandler(err);
    const messagesErrArr = errArr.map((str) => str.replace(/question_[^:\s]*:?\s*/g, ""));
    setErrorMessage(messagesErrArr.join(", "));
  };

  const fetchDynamicForm = useCallback(async () => {
    if (!document?.form) {
      return;
    }

    try {
      setIsLoading(true);
      setSuccessMessage(null);
      setErrorMessage(null);
      const form = await dynamicFormRepository.query({ formUrl: document.form });
      setDynamicForm(form);
    } catch (err) {
      console.error("Error fetching dynamic form:", err);
      handleErrorMessage(err);
    } finally {
      setIsLoading(false);
    }
  }, [document, dynamicFormRepository]);

  useEffect(() => {
    fetchDynamicForm();
  }, [fetchDynamicForm]);

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      await dynamicFormRepository.postForm(document?.form, formData);
      setSuccessMessage("Changes were saved successfully");
      await fetchDynamicForm();
      revalidate();
    } catch (err) {
      console.error("Error saving form data:", err);
      handleErrorMessage(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
    handleCloseAssociateAppointmentModal();
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const handleDocumentUpdated = useCallback(async () => {
    // Re-run actionable items loader first, then refresh document form data
    revalidate();
    await fetchDynamicForm();
  }, [fetchDynamicForm, revalidate]);

  return (
    <ModalShell
      open={openAssociateAppointmentsModal}
      setOpen={handleCloseAssociateAppointmentModal}
      fullWidth
      fullHeight
      customWidth="xl"
    >
      {dynamicForm?.formHeader && (
        <div className="w-full px-2 pb-2">
          <Title
            formHeader={dynamicForm?.formHeader}
            caseId={dynamicForm?.formHeader?.case_id}
            onDocumentUpdated={handleDocumentUpdated}
          />
          <Details formHeader={dynamicForm?.formHeader} />
        </div>
      )}
      <DynamicForm
        dynamicForm={dynamicForm}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        successMessage={successMessage}
        errorMessage={errorMessage}
        onCloseSuccess={handleCloseSuccess}
        onCloseError={handleCloseError}
      />
    </ModalShell>
  );
}
